import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { timestampToDate, getExcerpt, getReadTime, cleanTitle } from '../../utils/blogUtils';

const CENTRAL_API = import.meta.env.VITE_CENTRAL_API_URL || 'https://us-central1-paymentgateway-ab783.cloudfunctions.net/api';

interface BlogPost {
  id: string;
  title: string;
  content?: string;
  excerpt?: string;
  category: string;
  tags: string[];
  featuredImage?: string;
  featuredImageUrl?: string;
  urlSlug?: string;
  author?: string;
  readingTime?: number;
  wordCount?: number;
  publishedAt: { _seconds: number; _nanoseconds: number } | string;
}

const BlogListPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${CENTRAL_API}/content/public/blogs?limit=50`, { signal: controller.signal });
        if (!res.ok) throw new Error('Failed to fetch blog posts');
        const data = await res.json();
        setPosts(data.blogs || data || []);
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          setError('Blog service is temporarily unavailable. Please try again later.');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load posts');
        }
      } finally {
        clearTimeout(timeout);
        setLoading(false);
      }
    };
    fetchPosts();
    return () => { clearTimeout(timeout); controller.abort(); };
  }, []);

  const categories = useMemo(() => {
    const cats = new Set(posts.map((p) => p.category).filter(Boolean));
    return ['All', ...Array.from(cats).sort()];
  }, [posts]);

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      if (selectedCategory !== 'All' && p.category !== selectedCategory) return false;
      if (search) {
        const q = search.toLowerCase();
        const title = cleanTitle(p.title).toLowerCase();
        const exc = (p.excerpt || getExcerpt(p.content || '', 300)).toLowerCase();
        if (!title.includes(q) && !exc.includes(q)) return false;
      }
      return true;
    });
  }, [posts, selectedCategory, search]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-gray-300 dark:border-slate-600 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Blog</h1>
          <p className="text-lg text-gray-600 dark:text-slate-400 max-w-2xl mx-auto">
            Insights, tips, and updates on personal finance management and accounting.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === cat
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Posts Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 dark:text-slate-400">No posts found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((post) => {
              const date = timestampToDate(post.publishedAt);
              const slug = post.urlSlug || post.id;
              return (
                <Link
                  key={post.id}
                  to={`/blog/${slug}`}
                  className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden hover:shadow-md transition-shadow group"
                >
                  {(post.featuredImage || post.featuredImageUrl) && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.featuredImage || post.featuredImageUrl}
                        alt={cleanTitle(post.title)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      {post.category && (
                        <span className="px-2.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                          {post.category}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 dark:text-slate-400">
                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                      {cleanTitle(post.title)}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-slate-400 mb-4 line-clamp-3">
                      {post.excerpt || getExcerpt(post.content || '')}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-slate-500">
                      {post.author && <span>{post.author}</span>}
                      <span>{post.readingTime || getReadTime(post.content || '')} min read</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogListPage;
